import { useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { deepEqual } from "@tellescope/utils";
import { useCallback, useMemo, useState } from "react";
import {
  AccessAction,
  AccessLevel,
  DataAccessFeature,
  FieldRestrictionList,
  PHONE_CALL_REDACTION_MODEL,
  PortalSchemaRestrictionKey,
  RoleDraft,
  providerResourceAccess,
} from "./roleEditorConfig";

const draftFromRole = (role?: {
  role: string;
  color?: string;
  description?: string;
  permissions: RoleDraft["permissions"];
  uiRestrictions?: RoleDraft["uiRestrictions"];
  fieldRedactions?: RoleDraft["fieldRedactions"];
  portalSchemaRestrictions?: RoleDraft["portalSchemaRestrictions"];
}): RoleDraft => ({
  role: role?.role ?? "",
  color: role?.color,
  description: role?.description,
  permissions: role?.permissions ?? {},
  uiRestrictions: role?.uiRestrictions ?? {},
  fieldRedactions: role?.fieldRedactions ?? {},
  portalSchemaRestrictions: role?.portalSchemaRestrictions ?? {},
});

export const useEditRoleDialog = (roleId?: string) => {
  const roleQuery = useTellescopeQuery({
    model: "role_based_access_permissions",
    method: "getOne",
    request: roleId ?? "",
    enabled: !!roleId,
  });
  const role = roleQuery.data;

  const initialDraft = useMemo(() => draftFromRole(role), [role]);

  const [draft, setDraft] = useState<RoleDraft>(initialDraft);
  // Reset the draft whenever the dialog targets a different role (or the role finishes loading).
  const [syncedRoleId, setSyncedRoleId] = useState(role?.id);
  if (role?.id !== syncedRoleId) {
    setSyncedRoleId(role?.id);
    setDraft(initialDraft);
  }

  const updateRoleMutation = useTellescopeMutation({ model: "role_based_access_permissions", method: "updateOne" });

  const setRoleName = useCallback((name: string) => setDraft((prev) => ({ ...prev, role: name })), []);

  const setColor = useCallback((color: string) => setDraft((prev) => ({ ...prev, color: color || undefined })), []);

  const setDescription = useCallback(
    (description: string) => setDraft((prev) => ({ ...prev, description: description || undefined })),
    []
  );

  const setFeatureAccess = useCallback((feature: DataAccessFeature, action: AccessAction, value: AccessLevel) => {
    setDraft((prev) => {
      // Seed from the provider baseline so untouched actions keep their inherited values instead of
      // being silently downgraded to explicit NO_ACCESS the first time a resource is overridden.
      const permissions = feature.resources.reduce(
        (acc, resource) => ({
          ...acc,
          [resource]: { ...(acc[resource] ?? providerResourceAccess(resource)), [action]: value },
        }),
        { ...prev.permissions }
      );
      return { ...prev, permissions };
    });
  }, []);

  const setFeatureSidebar = useCallback((feature: DataAccessFeature, showInSidebar: boolean) => {
    setDraft((prev) => {
      const permissions = feature.resources.reduce(
        (acc, resource) => ({
          ...acc,
          [resource]: { ...(acc[resource] ?? providerResourceAccess(resource)), showInSidebar },
        }),
        { ...prev.permissions }
      );
      return { ...prev, permissions };
    });
  }, []);

  const toggleUIRestriction = useCallback((key: keyof RoleDraft["uiRestrictions"], checked: boolean) => {
    setDraft((prev) => ({ ...prev, uiRestrictions: { ...prev.uiRestrictions, [key]: checked } }));
  }, []);

  const setVisibleIntegrations = useCallback((values: string[]) => {
    setDraft((prev) => ({
      ...prev,
      uiRestrictions: { ...prev.uiRestrictions, visibleIntegrations: values.length > 0 ? values : undefined },
    }));
  }, []);

  const addFieldRestriction = useCallback((list: FieldRestrictionList) => {
    setDraft((prev) => ({
      ...prev,
      uiRestrictions: { ...prev.uiRestrictions, [list]: [...(prev.uiRestrictions[list] ?? []), {}] },
    }));
  }, []);

  const updateFieldRestriction = useCallback(
    (list: FieldRestrictionList, index: number, key: "type" | "field", value: string) => {
      setDraft((prev) => {
        const entries = [...(prev.uiRestrictions[list] ?? [])];
        const existing = entries[index];
        if (!existing) return prev;
        entries[index] = { ...existing, [key]: value || undefined };
        return { ...prev, uiRestrictions: { ...prev.uiRestrictions, [list]: entries } };
      });
    },
    []
  );

  const removeFieldRestriction = useCallback((list: FieldRestrictionList, index: number) => {
    setDraft((prev) => {
      const entries = (prev.uiRestrictions[list] ?? []).filter((_, i) => i !== index);
      return { ...prev, uiRestrictions: { ...prev.uiRestrictions, [list]: entries } };
    });
  }, []);

  const togglePortalSchemaRestriction = useCallback((key: PortalSchemaRestrictionKey, checked: boolean) => {
    setDraft((prev) => ({
      ...prev,
      portalSchemaRestrictions: { ...prev.portalSchemaRestrictions, [key]: checked },
    }));
  }, []);

  const toggleRedactionGroup = useCallback((fields: string[], checked: boolean) => {
    setDraft((prev) => {
      const current = new Set(prev.fieldRedactions[PHONE_CALL_REDACTION_MODEL] ?? []);
      fields.forEach((field) => (checked ? current.add(field) : current.delete(field)));
      const fieldRedactions = { ...prev.fieldRedactions };
      if (current.size > 0) fieldRedactions[PHONE_CALL_REDACTION_MODEL] = Array.from(current);
      else delete fieldRedactions[PHONE_CALL_REDACTION_MODEL];
      return { ...prev, fieldRedactions };
    });
  }, []);

  const isDirty = useMemo(() => !deepEqual(draft, initialDraft, { includeRemovals: true }), [draft, initialDraft]);
  const isValid = draft.role.trim().length > 0;
  const isRenamed = !!role && draft.role.trim() !== role.role;

  const save = useCallback(
    (onSuccess?: () => void) => {
      if (!role || !isValid) return;
      // Only send fields that actually changed so the request doesn't overwrite untouched values.
      const trimmedRole = draft.role.trim();
      const color = draft.color ?? "";
      const description = draft.description ?? "";

      const update: Parameters<typeof updateRoleMutation.mutate>[0] = { id: role.id };
      if (trimmedRole !== initialDraft.role) update.role = trimmedRole;
      if (color !== (initialDraft.color ?? "")) update.color = color;
      if (description !== (initialDraft.description ?? "")) update.description = description;
      if (!deepEqual(draft.permissions, initialDraft.permissions, { includeRemovals: true })) {
        update.permissions = draft.permissions;
      }
      if (!deepEqual(draft.uiRestrictions, initialDraft.uiRestrictions, { includeRemovals: true })) {
        update.uiRestrictions = draft.uiRestrictions;
      }
      if (!deepEqual(draft.fieldRedactions, initialDraft.fieldRedactions, { includeRemovals: true })) {
        update.fieldRedactions = draft.fieldRedactions;
      }
      if (
        !deepEqual(draft.portalSchemaRestrictions, initialDraft.portalSchemaRestrictions, { includeRemovals: true })
      ) {
        update.portalSchemaRestrictions = draft.portalSchemaRestrictions;
      }

      updateRoleMutation.mutate(update, { onSuccess });
    },
    [role, isValid, draft, initialDraft, updateRoleMutation]
  );

  return {
    role,
    draft,
    isDirty,
    isValid,
    isRenamed,
    isSaving: updateRoleMutation.isPending,
    setRoleName,
    setColor,
    setDescription,
    setFeatureAccess,
    setFeatureSidebar,
    toggleUIRestriction,
    setVisibleIntegrations,
    addFieldRestriction,
    updateFieldRestriction,
    removeFieldRestriction,
    toggleRedactionGroup,
    togglePortalSchemaRestriction,
    save,
  };
};

export type RoleEditor = ReturnType<typeof useEditRoleDialog>;
